import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useCallbackWithAnalytics } from '@atlaskit/analytics-next/useCallbackWithAnalytics';
import { Popup } from '@atlaskit/popup/popup';
import type { PopupProps } from '@atlaskit/popup/types';

import { NAVIGATION_CHANNEL } from '../../analytics';
import type { AccountMenuProps } from '../account-menu';
import {
	messages,
	type AccountMenuTriggerProps,
	type AccountMenuTriggerPropsNav4,
} from './account-menu-trigger';

export type AccountMenuPopupProps = Pick<AccountMenuProps, 'testId' | 'picture' | 'email'> &
	Pick<PopupProps, 'content'> & {
		// TODO: KIRBY-14227 - remove the Nav3 member when Nav3 is removed
		AccountMenuTrigger:
			| React.ComponentType<AccountMenuTriggerProps>
			| React.ComponentType<AccountMenuTriggerPropsNav4>;
		trustPicture?: boolean;
	};

export const AccountMenuPopup: React.ComponentType<AccountMenuPopupProps> = ({
	testId,
	picture,
	email,
	content,
	trustPicture = false,
	AccountMenuTrigger,
}) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const close = useCallbackWithAnalytics(
		() => setIsOpen(false),
		{
			eventType: UI_EVENT_TYPE,
			action: 'closed',
			actionSubject: 'popup',
			actionSubjectId: 'accountMenu',
		},
		NAVIGATION_CHANNEL,
	);
	const open = useCallbackWithAnalytics(
		() => setIsOpen(true),
		{
			eventType: UI_EVENT_TYPE,
			action: 'opened',
			actionSubject: 'popup',
			actionSubjectId: 'accountMenu',
		},
		NAVIGATION_CHANNEL,
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={close}
			testId={testId}
			placement="bottom"
			role="dialog"
			label={intl.formatMessage(messages.popupLabel)}
			trigger={(triggerProps) => (
				<AccountMenuTrigger
					onClick={isOpen ? close : open}
					testId={testId && `${testId}--trigger`}
					picture={picture}
					email={email}
					trustPicture={trustPicture}
					{...triggerProps}
					aria-haspopup="dialog"
				/>
			)}
			content={content}
		/>
	);
};
